import React, { useEffect, useState, useCallback } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { Loader2, Play, ExternalLink, Radio, FileText, Share2, Sparkles } from "lucide-react";
import api, { streamThumb } from "../lib/api";
import { useExternalLink } from "../context/ExternalLinkContext";
import { StreamPlayerModal, StreamShareModal } from "../components/StreamShare";
import { CoachTips } from "../components/CoachTip";

const PAGE = 18;

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  try { return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }); }
  catch { return ym; }
};

// Consistent, celebratory monthly growth note, names-free, always styled the same
// so members come to recognise "the monthly Signal".
const AnnouncementCard = ({ a }) => (
  <div data-testid="stream-announcement" className="relative overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-br from-[#0F2A28] via-[#0E1512] to-[#2A2410] p-7 mb-8 fade-up">
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#14A89A]/10 blur-2xl" />
    <div className="relative">
      <div className="flex items-center gap-2 mb-4">
        <span className="shrink-0 w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
          <Sparkles size={16} className="text-[#D4AF37]" />
        </span>
        <span className="min-w-0 font-body text-[10px] tracking-[0.2em] sm:tracking-[0.35em] uppercase text-[#D4AF37]">✦ The Monthly Signal · {monthLabel(a.month)}</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="font-heading text-5xl text-white leading-none" data-testid="announcement-new-count">{a.new_members}</span>
        <span className="font-heading text-xl text-[#14A89A]">new {a.new_members === 1 ? "sovereign" : "sovereigns"} joined</span>
      </div>
      <p className="font-body text-sm text-zinc-400 mt-3">The circle grows, deliberately. Welcome, and thank you to everyone who opened the door.</p>
      {a.top_referral_count > 0 && (
        <div className="inline-flex items-center gap-2 mt-4 border border-white/10 bg-black/20 px-3 py-1.5" data-testid="announcement-top-referral">
          <Sparkles size={13} className="text-[#D4AF37]" />
          <span className="font-body text-xs text-zinc-300">Our top sovereign welcomed <span className="text-[#D4AF37] font-medium">{a.top_referral_count}</span> this month</span>
        </div>
      )}
    </div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

const ShareBtn = ({ onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    data-testid="stream-share-button"
    title="Share to the Signal"
    className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/80 hover:text-[#14A89A] hover:border-[#14A89A]/60 opacity-0 group-hover:opacity-100 transition-all"
  >
    <Share2 size={14} />
  </button>
);

const VideoThumb = ({ item }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="relative aspect-video overflow-hidden bg-[#141414] border border-white/10 group-hover:border-[#14A89A]/50 transition-colors">
      {item.thumbnail && !broken ? (
        <img
          src={streamThumb(item.thumbnail)}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F2A28] to-[#141414]">
          <Radio size={28} className="text-[#14A89A]/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
        <span className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm border border-white/30 flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
          <Play size={18} className="text-white fill-white ml-0.5" />
        </span>
      </div>
    </div>
  );
};

const VideoCard = ({ item, onPlay, onShare }) => (
  <div className="group relative flex flex-col fade-up border border-white/10 hover:border-[#14A89A]/40 bg-[#101010] transition-colors">
    <ShareBtn onClick={() => onShare(item)} />
    <div className="flex items-center justify-between px-4 pt-4 pb-3">
      <div className="font-body text-[11px] tracking-[0.2em] uppercase text-[#14A89A] truncate">{item.source_name}</div>
      {item.published_at && <div className="font-body text-[11px] text-zinc-600 shrink-0 ml-3">{fmtDate(item.published_at)}</div>}
    </div>
    <button onClick={() => onPlay(item)} data-testid={`stream-video-${item.id}`} className="text-left flex flex-col">
      <VideoThumb item={item} />
      <div className="p-4">
        <h3 className="font-heading text-lg text-white leading-snug group-hover:text-[#2BC4B5] transition-colors">{item.title}</h3>
      </div>
    </button>
  </div>
);

const ArticleCard = ({ item, onOpen, onShare }) => (
  <div className="group relative flex flex-col fade-up border border-white/10 hover:border-[#A855F7]/50 bg-[#101010] transition-colors">
    <ShareBtn onClick={() => onShare(item)} />
    <button onClick={() => onOpen(item.url)} data-testid={`stream-article-${item.id}`} className="text-left flex flex-col flex-1">
      <div className="h-1 bg-gradient-to-r from-[#A855F7] to-[#14A89A]" />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-3">
          <span className="font-body text-[11px] tracking-[0.2em] uppercase text-[#A855F7] truncate">{item.source_name}</span>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {item.published_at && <span className="font-body text-[11px] text-zinc-600">{fmtDate(item.published_at)}</span>}
            <ExternalLink size={13} className="text-zinc-600 group-hover:text-[#A855F7] transition-colors" />
          </div>
        </div>
        <h3 className="font-heading text-xl text-white leading-snug group-hover:text-white/90 transition-colors">{item.title}</h3>
        {item.excerpt && <p className="font-body text-sm text-zinc-500 leading-relaxed mt-3 flex-1">{item.excerpt}</p>}
      </div>
    </button>
  </div>
);

export default function Stream() {
  const [kind, setKind] = useState("video");
  const [topic, setTopic] = useState("");
  const [topics, setTopics] = useState([]);
  const [items, setItems] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const { openExternal } = useExternalLink();

  useEffect(() => {
    api.get("/stream/topics").then(({ data }) => setTopics(data)).catch(() => setTopics([]));
    api.get("/stream/announcement").then(({ data }) => setAnnouncement(data)).catch(() => setAnnouncement(null));
  }, []);

  const load = useCallback(async () => {
    setItems(null);
    const params = { kind, skip: 0, limit: PAGE };
    if (topic) params.topic = topic;
    const { data } = await api.get("/stream", { params });
    setItems(data);
    setHasMore(data.length === PAGE);
  }, [kind, topic]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const params = { kind, skip: items.length, limit: PAGE };
      if (topic) params.topic = topic;
      const { data } = await api.get("/stream", { params });
      setItems((p) => [...p, ...data]);
      setHasMore(data.length === PAGE);
    } finally { setLoadingMore(false); }
  };

  const Toggle = ({ value, label, icon: Icon }) => {
    const on = kind === value;
    return (
      <button
        onClick={() => setKind(value)}
        data-testid={`stream-toggle-${value}`}
        className={`flex items-center gap-2 px-5 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${on ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}
      >
        <Icon size={15} /> {label}
      </button>
    );
  };

  // Only show chips that have content for the active kind.
  const visibleTopics = topics.filter((t) => (kind === "video" ? t.video : t.article) > 0);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8" data-testid="stream-page">
      <header className="mb-6">
        <div className="flex items-center gap-2.5 mb-1">
          <Radio size={22} className="text-[#14A89A]" strokeWidth={1.75} />
          <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">Conscious Stream</h1>
        </div>
        <p className="font-body text-sm text-zinc-500">A curated current from beyond the Signal, videos and writings worth your attention. Watched privately; Google never sees you here.</p>
      </header>

      <CoachTips screen="stream" />

      {announcement && announcement.new_members > 0 && <AnnouncementCard a={announcement} />}

      <div className="flex gap-2 mb-5" data-testid="stream-toggles">
        <Toggle value="video" label="Videos" icon={Play} />
        <Toggle value="article" label="Articles" icon={FileText} />
      </div>

      {visibleTopics.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-4 px-4 md:mx-0 md:px-0" data-testid="stream-topics">
          <button onClick={() => setTopic("")} data-testid="stream-topic-all" className={`shrink-0 px-4 py-1.5 font-body text-[11px] tracking-wider uppercase border transition-colors ${!topic ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>All</button>
          {visibleTopics.map((t) => {
            const on = topic === t.topic;
            return (
              <button key={t.topic} onClick={() => setTopic(t.topic)} data-testid={`stream-topic-${t.topic}`} className={`shrink-0 px-4 py-1.5 font-body text-[11px] tracking-wider uppercase border whitespace-nowrap transition-colors ${on ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                {t.topic}
              </button>
            );
          })}
        </div>
      )}

      {items === null ? (
        <div className="flex justify-center py-24"><SignalPulse size={44} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-white/10" data-testid="stream-empty">
          <p className="font-heading text-xl text-white mb-2">The current is still gathering</p>
          <p className="font-body text-sm text-zinc-500">Curated {kind === "video" ? "videos" : "articles"} {topic ? `on ${topic} ` : ""}will appear here soon.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-6" data-testid="stream-feed">
            {items.map((it) => kind === "video"
              ? <VideoCard key={it.id} item={it} onPlay={setPlaying} onShare={setSharing} />
              : <ArticleCard key={it.id} item={it} onOpen={openExternal} onShare={setSharing} />)}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-12">
              <button onClick={loadMore} disabled={loadingMore} data-testid="stream-load-more" className="border border-white/15 text-zinc-300 px-6 py-3 font-body text-sm hover:bg-white/5 transition-colors flex items-center gap-2 disabled:opacity-40">
                {loadingMore ? <Loader2 className="animate-spin" size={16} /> : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {playing && <StreamPlayerModal item={playing} onClose={() => setPlaying(null)} />}
      {sharing && <StreamShareModal item={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}
